import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { format } from 'date-fns';

const Spiffs = ({ user }) => {
  // Format date without timezone conversion (for date-only fields)
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return new Date(year, month - 1, day).toLocaleDateString();
  };

  const [spiffs, setSpiffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    to: new Date()
  });

  useEffect(() => {
    fetchSpiffs();
  }, [dateRange]);

  const fetchSpiffs = async () => {
    // Handle undefined dates from calendar
    if (!dateRange?.from || !dateRange?.to) return;
    
    try {
      const fromDate = format(dateRange.from, 'yyyy-MM-dd');
      const toDate = format(dateRange.to, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/spiffs?from_date=${fromDate}&to_date=${toDate}`, { withCredentials: true });
      setSpiffs(response.data);
    } catch (error) {
      toast.error('Failed to load spiffs');
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (range) => {
    // If only one date is selected, use it for both from and to
    if (range?.from && !range?.to) {
      setDateRange({ from: range.from, to: range.from });
    } else if (range?.from && range?.to) {
      setDateRange(range);
    }
  };

  const totalSpiff = spiffs.reduce((sum, s) => sum + s.spiffAmount, 0);

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '800' }}>Spiffs</h1>
        
        <Popover>
          <PopoverTrigger asChild>
            <button className="btn btn-secondary" data-testid="spiff-date-range-picker">
              <CalendarIcon size={16} />
              {dateRange?.from && dateRange?.to ? 
                `${format(dateRange.from, 'MMM dd')} - ${format(dateRange.to, 'MMM dd, yyyy')}` 
                : 'Select date range'}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={handleDateSelect}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="card" style={{ marginBottom: '32px', background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)', color: 'white' }}>
        <div style={{ fontSize: '16px', marginBottom: '8px', opacity: 0.9 }}>Total Spiff Earned (Completed Orders)</div>
        <div style={{ fontSize: '48px', fontWeight: '800' }} data-testid="total-spiff-earned">${totalSpiff.toFixed(2)}</div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Service</th>
              <th>Installation Date</th>
              <th>Spiff Amount</th>
            </tr>
          </thead>
          <tbody>
            {spiffs.map((spiff, idx) => (
              <tr key={idx} data-testid={`spiff-row-${idx}`}>
                <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{spiff.orderId.substring(0, 8)}</td>
                <td>{spiff.customerName}</td>
                <td>{spiff.serviceName}</td>
                <td>{formatDate(spiff.installationDate)}</td>
                <td style={{ fontWeight: '700', color: '#4CAF50', fontSize: '16px' }}>${spiff.spiffAmount}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {spiffs.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px', color: '#999' }}>
            No spiffs earned yet. Create and complete orders to earn spiffs!
          </div>
        )}
      </div>
    </div>
  );
};

export default Spiffs;
